import React from "react";
import TextInput from "../../components/common/inputs/textInput";
import ToggleInput from "../../components/common/inputs/toggleInput";
import ModalManager from "../../components/common/modal/ModalManager";
import Loading from "../../components/common/loading";
import { AppCard, AppPage, AppPageHeader } from "../../components/common/appShell";

import "./teamRoles.css";

const ROLE_PERMISSION_SECTIONS = [
  {
    label: "Basic Device Privileges",
    description: "Administrative controls for device identity and configuration.",
    permissions: [
      {
        title: "Edit device info",
        field: "editDeviceInfo",
        description:
          "Edit device information such as device name and configuration, including unpairing devices.",
      },
    ],
  },
  {
    label: "Inlet Power Sensors & Sensor Accessory Privileges",
    description: "Visibility and configuration rights for upstream power and sensors.",
    permissions: [
      {
        title: "View inlet info",
        field: "viewInletInfo",
        description: "View inlet name, state, power readings, and other inlet information.",
      },
      {
        title: "Edit inlet info",
        field: "editInletInfo",
        description:
          "Edit inlet names, energy counters, etc.",
      },
      {
        title: "View sensor accessory info",
        field: "viewSensorInfo",
        description: "View sensor names, temperature readings, humidity readings, and state.",
      },
    ],
  },
  {
    label: "Outlet Privileges",
    description: "Access for viewing, editing, and switching outlets.",
    permissions: [
      {
        title: "View all outlets",
        field: "viewAllOutletsAllowed",
        description: "View outlet name, state, power, and other outlet information.",
      },
      {
        title: "Edit all outlets",
        field: "editAllOutletsAllowed",
        description:
          "Edit outlet names, boot states, reboot intervals, and outlet-specific settings.",
      },
      {
        title: "Switch all outlets",
        field: "switchAllOutletsAllowed",
        description: "Switch outlets on or off, or power cycle outlets.",
      },
    ],
  },
  {
    label: "Event & Activity Privileges",
    description: "Access for logs, events, alerts, and action management.",
    permissions: [
      {
        title: "View activity",
        field: "viewActivityLog",
        description: "View activity logs and history for the device group.",
      },
      {
        title: "View events & actions",
        field: "viewEventInfo",
        description: "View events and alerts for the device group.",
      },
      {
        title: "Create, edit, and delete events & actions",
        field: "editEventInfo",
        description: "Manage events, alerts, and related actions for the device group.",
      },
    ],
  },
];

export default class UserRolePage extends React.Component {
  state = {
    description: "",
    name: "",
    viewDeviceInfo: true,
    editDeviceInfo: false,
    viewInletInfo: false,
    editInletInfo: false,
    viewSensorInfo: false,
    editSensorInfo: false,
    viewAllOutletsAllowed: false,
    editAllOutletsAllowed: false,
    switchAllOutletsAllowed: false,
    viewActivityLog: false,
    viewEventInfo: false,
    editEventInfo: false,
    hasChanges: false,
  };

  submit = () => {
    const isNewRole = this.props.match.params.roleId === "new";

    if (isNewRole) {
      this.createRole();
    } else {
      this.editRole();
    }
  };

  createRole = () => {
    const { hasChanges, ...roleObj } = this.state;
    this.props.createResource("roles", roleObj, (error) => {
      if (!error) {
        this.props.history.push("/team/roles");
      }
    });
  };

  editRole = () => {
    const { hasChanges, ...roleObj } = this.state;
    this.props.modifyResource(
      "roles",
      this.props.match.params.roleId,
      roleObj,
      (error) => {
        if (!error) {
          this.setState({ hasChanges: false });
        }
      },
    );
  };

  componentDidUpdate = (prevProps) => {
    if (this.props.resources.rolesLoaded && !prevProps.resources.rolesLoaded) {
      const foundRole = this.props.resources.roles.find(
        (role) => role.id == this.props.match.params.roleId,
      );
      if (foundRole) {
        this.setState({
          ...foundRole,
          hasChanges: false,
        });
      }
    }
  };

  componentDidMount = () => {
    if (
      !this.props.resources.rolesLoaded &&
      !this.props.resources.rolesLoading
    ) {
      this.props.fetchResource("roles");
    }
    const foundRole = this.props.resources.roles.find(
      (role) => role.id == this.props.match.params.roleId,
    );
    if (foundRole) {
      this.setState({
        ...foundRole,
        hasChanges: false,
      });
    }
  };

  deleteRole = () => {
    ModalManager.confirm(
      "Are you sure you want to permanently delete this role? All role assignments under this role will be void.",
      "Confirm Role Deletion",
      "Confirm",
      (status, close) => {
        if (status) {
          this.props.removeResource(
            "roles",
            this.props.match.params.roleId,
            (error) => {
              if (!error) {
                close && close();
                this.props.history.push("/team/roles");
              }
            },
          );
        }
      },
    );
  };

  updateField = (field, value) => {
    this.setState({
      [field]: value,
      hasChanges: true,
    });
  };

  renderPermissionSection = (section) => {
    const enabledCount = section.permissions.filter(
      (permission) => this.state[permission.field],
    ).length;

    return (
      <section className="roles-permission-section" key={section.label}>
        <div className="roles-permission-section__header">
          <div>
            <h3>{section.label}</h3>
            <p>{section.description}</p>
          </div>
          <span className="roles-permission-count">
            {enabledCount}/{section.permissions.length} enabled
          </span>
        </div>
        <div className="roles-permission-list">
          {section.permissions.map((item) => (
            <div
              className={`roles-permission-editor-row ${
                this.state[item.field] ? "is-enabled" : ""
              }`}
              key={item.field}
            >
              <div className="roles-permission-editor-copy">
                <div className="roles-permission-editor-title">{item.title}</div>
                <div className="roles-permission-editor-description">
                  {item.description}
                </div>
              </div>
              <ToggleInput
                value={this.state[item.field]}
                callback={(value) => this.updateField(item.field, value)}
                id={item.field}
                inputContainerStyle={{
                  margin: "0px",
                  padding: "0px",
                  width: "124px",
                }}
                toggleLabelOff="Off"
                toggleLabelOn="On"
              />
            </div>
          ))}
        </div>
      </section>
    );
  };

  render() {
    const isNewRole = this.props.match.params.roleId === "new";
    const loading = this.props.resources.rolesLoading;

    if (loading) {
      return <Loading black />;
    }

    return (
      <AppPage className="roles-page">
        <AppPageHeader
          // eyebrow="Team"
          title={isNewRole ? "Create Role" : "Edit Role"}
          description="Define the reusable permissions granted when this role is assigned to a user or device group."
          actions={
            <>
              <button
                type="button"
                className="button secondary roles-button-link"
                onClick={() => this.props.history.push("/team/roles")}
              >
                <span className="material-icons roles-button-icon" aria-hidden="true">
                  arrow_back
                </span>
                Back to Roles
              </button>
              {!isNewRole && (
                <button
                  type="button"
                  className="button alert roles-button-link"
                  onClick={() => this.deleteRole()}
                >
                  <span className="material-icons roles-button-icon" aria-hidden="true">
                    delete
                  </span>
                  Delete Role
                </button>
              )}
              <button
                type="button"
                className="button roles-button-link"
                onClick={() => this.submit()}
              >
                <span className="material-icons roles-button-icon" aria-hidden="true">
                  save
                </span>
                {isNewRole ? "Create Role" : "Save Changes"}
              </button>
            </>
          }
        />

        <div className="roles-form">
          <AppCard
            title="Role Details"
            description="Name the role and describe when it should be assigned."
          >
            <div className="roles-field-grid">
              <TextInput
                value={this.state.name}
                callback={(value) => this.updateField("name", value)}
                label="Role Name"
                placeholder="Example: Technician Level 1"
              />
              <TextInput
                value={this.state.description}
                callback={(value) => this.updateField("description", value)}
                label="Description"
                placeholder="Example: Can view device info and switch outlets."
              />
            </div>
          </AppCard>

          <AppCard
            title="Permissions"
            description="Enable only the permissions this role should contribute. Assignments are additive."
          >
            <div className="roles-permission-editor">
              {ROLE_PERMISSION_SECTIONS.map((section) =>
                this.renderPermissionSection(section),
              )}
            </div>
          </AppCard>
        </div>
      </AppPage>
    );
  }
}
